/**
 * HostCard.js
 *
 * Shown to the host (an admin) on their own pass instead of the RSVP buttons: they're always on board.
 * "Check" opens the guest list: who's coming, who isn't, and who hasn't replied yet.
 */

import React, { useState } from "react";
import { Check, ChevronUp, Clock, ListChecks, PlaneTakeoff, RefreshCw, X } from "lucide-react";
import { getRsvps } from "../helpers/flyApi";

const PAPER = "#FBF7EF";
const INK = "#1B2A41";
const MUTED = "#5B6577";
const LINE = "#E2DCCF";
const MONO = "'JetBrains Mono', monospace";

// The three groups, in the order the backend sorts them
const GROUPS = [
    { rsvp: true,  title: "On board",       countKey: "coming",     icon: Check },
    { rsvp: false, title: "Can't make it",  countKey: "notComing",  icon: X },
    { rsvp: null,  title: "No reply yet",   countKey: "noReply",    icon: Clock },
];

/**
 * One group of guests, e.g. everyone on board
 */
const GuestGroup = ({ group, guests, count, accent }) => {
    const Icon = group.icon;
    return (
        <div className="flex flex-col">
            <p className="flex items-center gap-2 text-[11px] font-bold tracking-[0.18em] uppercase pb-2" style={{ color: accent, borderBottom: `1px solid ${LINE}` }}>
                <Icon size={14} aria-hidden="true" /> {group.title} · {count}
            </p>
            {guests.length === 0 && <p className="py-2 text-sm" style={{ color: MUTED }}>Nobody</p>}
            {guests.map((guest) => (
                <div key={guest.pnr} className="flex items-center justify-between gap-3 py-2 text-sm" style={{ borderBottom: `1px solid ${LINE}` }}>
                    <span className="flex flex-col min-w-0">
                        <span className="font-medium">{guest.fullName}</span>
                        {!guest.claimed && <span className="text-xs" style={{ color: MUTED }}>Hasn't opened their pass yet</span>}
                    </span>
                    <span className="text-xs font-bold shrink-0" style={{ fontFamily: MONO, color: MUTED }}>{guest.pnr}</span>
                </div>
            ))}
        </div>
    );
};

/**
 * @param {Object} booking      -   { status: 'owner', isAdmin: true, pass } from the backend
 * @returns {JSX.Element}
 */
const HostCard = ({ booking }) => {
    const accent = booking.pass.colour;
    const [open, setOpen] = useState(false);
    // { state: 'loading' | 'loaded' | 'error', guests?, counts?, error? }
    const [list, setList] = useState({ state: "loading" });

    const load = async () => {
        setList({ state: "loading" });
        try {
            const { guests, counts } = await getRsvps();
            setList({ state: "loaded", guests, counts });
        } catch (err) {
            setList({ state: "error", error: err.message || "Couldn't load the guest list" });
        }
    };

    const toggle = () => {
        if (!open) load();
        setOpen(!open);
    };

    return (
        <div
            className="w-full flex gap-3 sm:gap-4 rounded-2xl px-4 py-4 sm:px-5 text-left"
            style={{ background: PAPER, color: INK, borderLeft: `6px solid ${accent}`, boxShadow: "0 6px 18px rgba(27,42,65,0.10)" }}
        >
            <PlaneTakeoff className="shrink-0 mt-0.5" size={22} color={accent} aria-hidden="true" />
            <div className="flex flex-col gap-3 flex-1 min-w-0">
                <div>
                    <p className="text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: accent }}>RSVP · Captain</p>
                    <p className="font-bold tracking-wide">You're the captain, so you're on board.</p>
                    <p className="text-sm" style={{ color: MUTED }}>Check who else is coming.</p>
                </div>
                <div className="flex gap-2 sm:gap-3">
                    <button
                        type="button"
                        onClick={toggle}
                        aria-expanded={open}
                        className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs sm:text-sm font-bold tracking-[0.12em] transition hover:opacity-90"
                        style={{ background: accent, color: PAPER }}
                    >
                        {open ? <ChevronUp size={16} aria-hidden="true" /> : <ListChecks size={16} aria-hidden="true" />}
                        {open ? "HIDE" : "CHECK"}
                    </button>
                    {open && (
                        <button
                            type="button"
                            onClick={load}
                            disabled={list.state === "loading"}
                            className="inline-flex items-center gap-2 rounded-full border-2 px-4 py-2 text-xs font-bold tracking-[0.12em] transition hover:opacity-80 disabled:opacity-50"
                            style={{ borderColor: accent, color: accent }}
                        >
                            <RefreshCw size={14} aria-hidden="true" /> REFRESH
                        </button>
                    )}
                </div>

                {open && list.state === "loading" && <p className="text-sm animate-pulse" style={{ color: MUTED }}>Loading the guest list…</p>}
                {open && list.state === "error" && <p className="text-sm" style={{ color: "#A8183D" }}>{list.error}</p>}
                {open && list.state === "loaded" && (
                    <div className="flex flex-col gap-5 pt-1">
                        {GROUPS.map((group) => (
                            <GuestGroup
                                key={group.countKey}
                                group={group}
                                guests={list.guests.filter((guest) => guest.rsvp === group.rsvp)}
                                count={list.counts[group.countKey]}
                                accent={accent}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default HostCard;
